import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import cl from 'classnames';
import _ from 'lodash';
import UtilsHelper from '../../utils/utils-helper';
import {t} from '../../services/i18nHandler';
import {tariffImageByName} from '../../constants/tariffs';

function TariffItem({
  tariff, activeTariff, setActiveTariff, detailsClick, language, activePeriod, detailsDemoClick, isIndividual,
}) {
  const tariffItem = useRef();

  const active = useMemo(() => (activeTariff === tariff._id), [tariff, activeTariff]);

  const isDemo = useMemo(() => (tariff.giveAtRegister), [tariff]);

  const isPopular = useMemo(() => (tariff.popular), [tariff]);

  const isSoon = useMemo(() => (tariff.soon), [tariff]);

  const name = useMemo(() => (tariff.name || {}), [tariff]);

  const shortDescription = useMemo(() => (tariff.shortDescription || {}), [tariff]);

  const tariffImage = useMemo(() => (tariffImageByName(name.ru)), [tariff, name]);

  const handleClickOutside = useCallback(({target}) => {
    if ((activeTariff && !active)) {
      return;
    }
    if (target.className) {
      const clList = target.classList;
      if (clList.contains('tariffs_period-switch_item')) {
        return;
      }
    }
    if (tariffItem.current && !tariffItem.current.contains(target)) {
      setActiveTariff(null);
    }
  }, [setActiveTariff, activeTariff, tariff]);

  const onItemClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveTariff(tariff._id);
  }, [tariff, active]);

  const priceDimension = useMemo(() => {
    const pd = tariff.priceDimension;
    if (pd === 1) {
      return t('units.rubles');
    }
    if (pd === 2) {
      return t('units.rublesMonth');
    }
    return null;
  }, [tariff, language]);

  const basePrice = useMemo(() => (tariff.basePrice || 0), [tariff]);

  const currentPeriod = useMemo(() => {
    const periods = tariff.pricePeriods || [];
    return _.find(periods, {months: activePeriod}) || {};
  }, [tariff, activePeriod]);

  const amount = useMemo(() => {
    const amountValue = currentPeriod.amount || tariff.basePrice || 0;
    return UtilsHelper.priceFormat(amountValue);
  }, [currentPeriod, tariff, activePeriod]);

  const discountText = useMemo(() => {
    const {discount, prefix} = currentPeriod;
    if (discount) {
      return (
        <>
          {t('tariffs.discount')}
          {prefix === 1 && (
            <>
              &nbsp;
              {t('tariffs.moreThan')}
            </>
          )}
          &nbsp;
          {discount}
          %
        </>
      );
    }
    const subNameText = tariff.subNameText || {};
    return subNameText[language];
  }, [tariff, language, currentPeriod]);

  const totalDiscount = useMemo(() => {
    const currentAmount = currentPeriod.amount;
    if (!currentAmount || currentAmount === basePrice) {
      return null;
    }
    if (basePrice) {
      const totalDiscountValue = basePrice - currentAmount;
      return UtilsHelper.priceFormat(totalDiscountValue);
    }
    return null;
  }, [basePrice, currentPeriod]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeTariff]);

  return (
    <div
      role="button"
      className={cl('tariffs_item_wrap', {active})}
      onClick={onItemClick}
      ref={tariffItem}
    >
      <div className={cl('tariffs_item', {
        active,
        demo: isDemo,
      })}
      >
        {isDemo && (
          <div className="tariffs_item_demo" role="button" onClick={detailsDemoClick}>
            {t('tariffs.freeDemoAccess')}
            <i className="tariffs_item_demo_info" />
          </div>
        )}
        {isSoon && (
          <div className={`tariffs_item_soon ${language}`} />
        )}
        {isPopular && (
          <div className="tariffs_item_popular">
            <div className="tariffs_item_popular_text">{t('tariffs.popular')}</div>
          </div>
        )}
        <div className="tariffs_item_image">
          <img src={tariffImage} alt={name[language]} />
        </div>
        <div className="tariffs_item_name">
          {name[language]}
        </div>
        <div className="tariffs_item_price">
          {!isIndividual && (
            <>
              <div className="tariffs_item_price_value">
                {amount}
              </div>
              <div className="tariffs_item_price_unit">{priceDimension}</div>
            </>
          )}
          {isIndividual && (
            <div className="tariffs_item_price_text">{t('tariffs.individual')}</div>
          )}
        </div>
        <div className="tariffs_item_discount">
          <div className="tariffs_item_discount_value">
            {discountText}
          </div>
          {totalDiscount && (
            <div className="tariffs_item_discount_total">
              {t('tariffs.totalDiscount')}
              &nbsp;
              <strong>
                {totalDiscount}
                &nbsp;
                {priceDimension}
              </strong>
            </div>
          )}
        </div>
        <div className="tariffs_item_info">
          {shortDescription[language]}
        </div>
        <div className="tariffs_item_btn-wrap">
          {isIndividual && (
            <button
              onClick={detailsClick}
              type="button"
              className="tariffs_item_btn"
            >
              {t('tariffs.contactUs')}
            </button>
          )}
          {!isIndividual && (
            <button
              onClick={detailsClick}
              type="button"
              className="tariffs_item_btn"
            >
              {t('tariffs.details')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

TariffItem.defaultProps = {
  detailsClick: () => {},
  detailsDemoClick: () => {},
  setActiveTariff: () => {},
  tariff: {},
  language: 'ru',
  activePeriod: 1,
  activeTariff: '',
};

TariffItem.propTypes = {
  detailsClick: PropTypes.func,
  detailsDemoClick: PropTypes.func,
  setActiveTariff: PropTypes.func,
  activePeriod: PropTypes.number,
  language: PropTypes.string,
  activeTariff: PropTypes.string,
  tariff: PropTypes.shape({}),
};

export default TariffItem;
